<script setup lang="ts">
import { Eye } from 'lucide-vue-next'

import type { Component } from 'vue'
import type { Audience } from '@/types/types'

defineProps<{
  id: string
  icon: Component
  label: string
  description: string
  emptyError: string
  audiences: Audience[]
  isPreviewing: boolean
}>()

const { t } = useI18n()

const emit = defineEmits<{
  'toggle-preview': []
}>()

const isEnabled = defineModel<boolean>('enabled', { required: true })
const audienceIds = defineModel<number[]>('audienceIds', { required: true })
</script>

<template>
  <div
    class="border-border flex flex-col rounded-xl border p-3 transition-colors"
    :class="isEnabled ? 'bg-background shadow-surface' : 'bg-muted/50'"
  >
    <div class="flex items-center gap-3">
      <label :for="id" class="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
        <span
          class="flex size-10 shrink-0 items-center justify-center rounded-xl"
          :class="isEnabled ? 'bg-muted' : 'bg-background'"
        >
          <component :is="icon" class="size-4.5" :class="isEnabled ? 'text-primary' : 'text-foreground-disabled'" />
        </span>
        <span class="flex min-w-0 flex-1 flex-col">
          <span class="text-sm font-medium" :class="isEnabled ? 'text-foreground' : 'text-foreground-secondary'">
            {{ label }}
          </span>
          <span v-if="$slots.badge" class="mt-1 flex"><slot name="badge" /></span>
          <span class="text-muted-foreground text-xs">{{ description }}</span>
        </span>
      </label>
      <AppTooltip
        v-if="isEnabled && audienceIds.length > 0"
        :text="
          isPreviewing
            ? t('pages.loyaltySupports.webapp.editor.audience.previewOff')
            : t('pages.loyaltySupports.webapp.editor.audience.previewOn')
        "
      >
        <Button
          :variant="isPreviewing ? 'ghost-primary' : 'ghost'"
          size="icon-sm"
          :aria-pressed="isPreviewing"
          :aria-label="
            isPreviewing
              ? t('pages.loyaltySupports.webapp.editor.audience.previewOff')
              : t('pages.loyaltySupports.webapp.editor.audience.previewOn')
          "
          @click="emit('toggle-preview')"
        >
          <Eye />
        </Button>
      </AppTooltip>
      <Switch :id="id" v-model="isEnabled" />
    </div>

    <div v-if="isEnabled" class="border-border mt-3 flex flex-col gap-2 border-t pt-3">
      <Label>{{ t('pages.loyaltySupports.webapp.editor.audience.concerned') }}</Label>
      <MultiSelectAudiences
        v-model="audienceIds"
        :audiences="audiences"
        hide-empty-categories
        :default-text="t('pages.loyaltySupports.webapp.editor.audience.choose')"
      />
      <p v-if="audienceIds.length === 0" class="text-destructive text-xs">{{ emptyError }}</p>
    </div>
  </div>
</template>
